/**
 * Recover from stale dynamic-import (chunk) failures after a redeploy.
 *
 * When a new build ships, the user's cached index.html / service worker can
 * still reference old hashed chunk URLs. The next lazy() route fetch 404s
 * with "Failed to fetch dynamically imported module" and the app dies in the
 * ErrorBoundary. We detect that signature and force a one-shot clean reload.
 */

const FLAG = "__chunk_reload__";

export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg =
    (err instanceof Error ? err.message : typeof err === "string" ? err : "") || "";
  const name = err instanceof Error ? err.name : "";
  return (
    name === "ChunkLoadError" ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Loading chunk \S+ failed/i.test(msg) ||
    /Loading CSS chunk \S+ failed/i.test(msg)
  );
}

export function recoverFromChunkError(): void {
  if (typeof window === "undefined") return;
  try {
    if (sessionStorage.getItem(FLAG)) return; // already attempted this session
    sessionStorage.setItem(FLAG, String(Date.now()));
  } catch {
    /* private mode — proceed without guard */
  }

  const reload = () => {
    try {
      window.location.reload();
    } catch {
      /* ignore */
    }
  };

  const tasks: Promise<unknown>[] = [];
  try {
    if ("serviceWorker" in navigator) {
      tasks.push(
        navigator.serviceWorker
          .getRegistrations()
          .then((regs) => Promise.allSettled(regs.map((r) => r.unregister())))
          .catch(() => undefined),
      );
    }
  } catch {
    /* ignore */
  }
  try {
    if (typeof caches !== "undefined") {
      tasks.push(
        caches
          .keys()
          .then((keys) => Promise.allSettled(keys.map((k) => caches.delete(k))))
          .catch(() => undefined),
      );
    }
  } catch {
    /* ignore */
  }

  Promise.allSettled(tasks).then(reload, reload);
  // Hard fallback so we don't hang if SW/caches APIs stall.
  setTimeout(reload, 1500);
}

/** Clear the once-per-session guard after a successful boot. */
export function clearChunkReloadGuard(delayMs = 10_000): void {
  if (typeof window === "undefined") return;
  setTimeout(() => {
    try {
      sessionStorage.removeItem(FLAG);
    } catch {
      /* ignore */
    }
  }, delayMs);
}

export function installChunkReloadHandlers(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (e) => {
    if (isChunkLoadError(e.error) || isChunkLoadError(e.message)) {
      recoverFromChunkError();
    }
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (isChunkLoadError(e.reason)) {
      recoverFromChunkError();
    }
  });
}